import AppContext from 'App/AppContext';
import { IPSpinner, IPStack, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import { globalStackTokensGapSmall } from 'globalStyles';
import { FormUpgradePreview } from 'models/tasks/formVersion';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetFormUpgradePreview, apiUpgradeFormTasks } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';

interface IFormUpgradeModalProps {
  taskTypeFamilyId: number;
  // set: only the open instances of this recurring series master; unset: every open task of the form
  taskMasterId?: number;
  isOpen: boolean;
  onDismiss: () => void;
  onUpgraded?: (upgradedCount: number) => void;
}

// Moves the open, unanswered tasks that still use an older version of a form to its current version.
// The preview counts what moves and what stays (a series can run into hundreds of instances, so the
// tasks are not listed); the modal owns the mutation.
const FormUpgradeModal = (props: IFormUpgradeModalProps) => {
  const { t } = useTranslation(['forms']);
  const appContext = useContext(AppContext);
  const [preview, setPreview] = useState<FormUpgradePreview | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const loadPreview = async () => {
      try {
        setIsLoading(true);
        setPreview(undefined);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const result = await apiGetFormUpgradePreview(props.taskTypeFamilyId, props.taskMasterId, accessToken);
        setPreview(result);
      } catch (error) {
        appContext.setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadPreview();
  }, [appContext, props.isOpen, props.taskTypeFamilyId, props.taskMasterId]);

  const hasUpgradable = (preview?.upgradableCount ?? 0) > 0;
  //every task pinned behind the head is in exactly one bucket, so this is what the dialog can explain
  const behindHead = preview
    ? preview.upgradableCount + preview.answeredCount + preview.closedCount + preview.inaccessibleCount
    : 0;

  const upgrade = async () => {
    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiUpgradeFormTasks(props.taskTypeFamilyId, props.taskMasterId, accessToken);
      //a toast raised from a dialog goes to the bottom container
      appContext.showNotification(
        t('forms:Versioning.Upgrade.Updated', { count: result.upgradedCount }),
        false,
        false,
        true,
      );
      props.onUpgraded?.(result.upgradedCount);
      props.onDismiss();
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsSaving(false);
    }
  };

  //when no task is behind the head, the sentence that says so is the whole dialog; when tasks are
  //behind it but none moves, the counts below say why each one stays
  const getSubText = (): string => {
    if (!preview) return '';
    if (behindHead === 0) return t('forms:Versioning.Upgrade.Preview.None');
    if (!hasUpgradable) {
      return t('forms:Versioning.Upgrade.Preview.NoneKept', { name: preview.name ?? '', version: preview.version });
    }

    return t('forms:Versioning.Upgrade.Text', { name: preview.name ?? '', version: preview.version });
  };

  const renderCounts = (loaded: FormUpgradePreview): JSX.Element => (
    <IPStack tokens={globalStackTokensGapSmall}>
      {loaded.upgradableCount > 0 && (
        <IPStack.Item>
          <IPText>{t('forms:Versioning.Upgrade.Preview.Upgradable', { count: loaded.upgradableCount })}</IPText>
        </IPStack.Item>
      )}
      {loaded.answeredCount > 0 && (
        <IPStack.Item>
          <IPText>{t('forms:Versioning.Upgrade.Preview.KeptAnswered', { count: loaded.answeredCount })}</IPText>
        </IPStack.Item>
      )}
      {loaded.closedCount > 0 && (
        <IPStack.Item>
          <IPText>{t('forms:Versioning.Upgrade.Preview.KeptClosed', { count: loaded.closedCount })}</IPText>
        </IPStack.Item>
      )}
      {loaded.inaccessibleCount > 0 && (
        <IPStack.Item>
          <IPText>{t('forms:Versioning.Upgrade.Preview.Inaccessible', { count: loaded.inaccessibleCount })}</IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );

  const showCounts = preview !== undefined && !isLoading && behindHead > 0;

  //the preview area keeps its height while the counts load, so the dialog does not collapse to the
  //title and buttons and then jump when the counts arrive; the spinner is centered in that area
  const renderPreview = (): JSX.Element => (
    <IPStack
      verticalAlign={isLoading ? 'center' : 'start'}
      horizontalAlign={isLoading ? 'center' : 'stretch'}
      styles={{ root: { minHeight: 100 } }}
    >
      {isLoading && <IPSpinner size={IPSpinnerSize.medium} />}
      {showCounts && preview && renderCounts(preview)}
    </IPStack>
  );

  return (
    <DialogYesNo
      hidden={!props.isOpen}
      title={t('forms:Versioning.Upgrade.Title')}
      subText={getSubText()}
      yesText={t('forms:Versioning.Upgrade.Confirm')}
      disableYes={isLoading || isSaving || !hasUpgradable}
      optionalJSX={renderPreview()}
      optionalJSXGrow={true}
      dialogStyles={{ main: { maxWidth: 560 } }}
      onYes={() => void upgrade()}
      onNo={props.onDismiss}
    />
  );
};

export default FormUpgradeModal;
